import { Popover as AntPopover, PopoverProps as AntPropoverProps } from 'antd'
import styled, { createGlobalStyle, css } from 'styled-components'

import { findScrollableParent } from '@/utils/findScrollableParent'

const PopoverGlobalStyle = createGlobalStyle`
  .bp-popover-overlay {
    &.no-arrow {
      padding-top: 0 !important; // get closer to the trigger element
      padding-bottom: 0 !important; // get closer to the trigger element

      & .ant-popover-arrow {
        display: none;
      }
    }

    &.ant-popover-placement-bottom,
    &.ant-popover-placement-bottomLeft,
    &.ant-popover-placement-bottomRight {
      & .ant-popover-arrow {
        top: 1px; // move down 1px to account for the border
      }
    }

    &.ant-popover-placement-top,
    &.ant-popover-placement-topLeft,
    &.ant-popover-placement-topRight {
      & .ant-popover-arrow {
          bottom: 1px; // move up 1px to account for the border
      }
    }

    &.ant-popover-placement-right,
    &.ant-popover-placement-rightTop,
    &.ant-popover-placement-rightBottom {
      & .ant-popover-arrow {
          left: 1px; // move left 1px to account for the border
      }
    }

    &.ant-popover-placement-left,
    &.ant-popover-placement-leftTop,
    &.ant-popover-placement-leftBottom {
      & .ant-popover-arrow {
          right: 1px; // move right 1px to account for the border
      }
    }

    & .ant-popover-inner {
      border-radius: ${({ theme }) => theme.borderRadius.small};
      overflow: hidden;
      border: 1px solid ${({ theme }) => theme.colour.neutral.border};
      box-shadow: 0px 3px 22px -7px rgba(41, 43, 60, 0.4);


      & .ant-popover-inner-content {
        padding: 0;
      }
    }
  }
`

export const POPOVER_ITEM_PADDING = `0.625rem 0.5rem 0.625rem 1rem`

export const PopoverItemNaked = styled.div`
  padding: ${POPOVER_ITEM_PADDING};
`

PopoverItemNaked.displayName = 'PopoverItemNaked' // for BUI preview reasons

type ItemProps = {
  danger?: boolean
  active?: boolean
}

export const PopoverItem = styled.a<ItemProps>`
  ${({ theme }) => theme.font.body03};
  color: ${({ theme }) => theme.colour.secondary._800};
  padding: ${POPOVER_ITEM_PADDING};
  display: flex;
  gap: 0.625rem;
  align-items: center;
  cursor: pointer;

  &:first-child {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }

  &:last-child {
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  & svg {
    font-size: 1rem;
    color: ${({ theme }) => theme.colour.secondary._400};
  }

  &:hover,
  &:focus,
  &:active {
    color: ${({ theme }) => theme.colour.secondary._800};
    background-color: ${({ theme }) => theme.colour.neutral.surface};
  }

  ${({ danger }) =>
    danger &&
    css`
      color: ${({ theme }) => theme.colour.semantic.error};

      & svg {
        color: ${({ theme }) => theme.colour.semantic.error};
      }

      &:hover,
      &:focus,
      &:active {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: ${({ theme }) => theme.colour.semantic.errorLight};

        & svg {
          color: ${({ theme }) => theme.colour.semantic.errorDark};
        }
      }
    `}

  ${({ active, theme }) =>
    active &&
    css`
      background-color: ${theme.colour.primary._50};
    `}
`

PopoverItem.displayName = 'PopoverItem' // for BUI preview reasons

export const PopoverDivider = styled.hr`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colour.neutral.border};
  margin-top: 0;
  margin-bottom: 0;
`

export type PopoverProps = AntPropoverProps & {
  showArrow?: boolean
}

export const Popover: React.FC<PopoverProps> = ({
  children,
  overlayStyle,
  overlayInnerStyle,
  showArrow = true,
  getPopupContainer,
  ...rest
}) => {
  return (
    <>
      <PopoverGlobalStyle />
      <AntPopover
        trigger={['click']}
        getPopupContainer={getPopupContainer ?? findScrollableParent}
        {...rest}
        overlayClassName={`bp-popover-overlay ${showArrow ? '' : ' no-arrow'}`}
        overlayStyle={{ minWidth: '220px', maxWidth: '100vw', ...overlayStyle }}
        overlayInnerStyle={{
          maxHeight: '600px',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          ...overlayInnerStyle,
        }}
      >
        {children}
      </AntPopover>
    </>
  )
}

Popover.displayName = 'Popover'
